import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { XCircle, AlertCircle, TrendingUp } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface ATSIssuesListProps {
  criticalIssues: Array<{ issue: string; location: string; fix: string }>;
  warnings: Array<{ issue: string; suggestion: string }>;
  opportunities: string[];
}

export const ATSIssuesList = ({ criticalIssues, warnings, opportunities }: ATSIssuesListProps) => {
  return (
    <div className="space-y-4">
      {/* Critical Issues */}
      {criticalIssues.length > 0 && (
        <Card className="p-6 border-destructive/50">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-destructive">
            <XCircle className="h-6 w-6" />
            Critical Issues ({criticalIssues.length})
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            These issues will likely cause ATS rejection. Fix these first.
          </p>
          <Accordion type="single" collapsible className="w-full">
            {criticalIssues.map((issue, index) => (
              <AccordionItem key={index} value={`critical-${index}`}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-start gap-3 text-left">
                    <Badge variant="destructive" className="mt-1">Critical</Badge>
                    <span className="font-medium">{issue.issue}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pl-20">
                    <div>
                      <span className="font-semibold text-sm">Location:</span>
                      <p className="text-muted-foreground">{issue.location}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-sm">How to Fix:</span>
                      <p className="text-muted-foreground">{issue.fix}</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Card className="p-6 border-yellow-500/50">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-yellow-600">
            <AlertCircle className="h-6 w-6" />
            Warnings ({warnings.length})
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            These might cause parsing issues or reduce your score.
          </p>
          <div className="space-y-3">
            {warnings.map((warning, index) => (
              <div key={index} className="p-4 bg-muted/50 border border-yellow-600/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Badge variant="outline" className="border-yellow-600 text-yellow-600 dark:border-yellow-500 dark:text-yellow-500 mt-1">
                    Warning
                  </Badge>
                  <div className="flex-1">
                    <p className="font-medium text-foreground mb-2">{warning.issue}</p>
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Suggestion:</strong> {warning.suggestion}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Opportunities */}
      {opportunities.length > 0 && (
        <Card className="p-6 border-primary/50">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-primary">
            <TrendingUp className="h-6 w-6" />
            Optimization Opportunities ({opportunities.length})
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Improvements that will boost your score and ATS compatibility.
          </p>
          <div className="grid gap-3">
            {opportunities.map((opportunity, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <TrendingUp className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{opportunity}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {criticalIssues.length === 0 && warnings.length === 0 && opportunities.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">No issues found! Your resume looks great.</p>
        </Card>
      )}
    </div>
  );
};
